import { ChangeDetectionStrategy, Component, effect, inject, Injector } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AuthService } from './core/auth/auth.service';
import { TranslocoPipe } from '@jsverse/transloco';
import type { PushNotificationService } from './core/pwa/push-notification.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, TranslocoPipe],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  protected readonly authService = inject(AuthService);
  private readonly injector = inject(Injector);
  private pushNotificationsPromise: Promise<PushNotificationService> | null = null;

  constructor() {
    effect(() => {
      if (this.authService.isInitializing()) {
        return;
      }

      void this.syncPushIdentity(this.authService.accountId());
    });
  }

  private async syncPushIdentity(accountId: string | null): Promise<void> {
    try {
      const pushNotifications = await this.loadPushNotifications();
      await pushNotifications.syncIdentity(accountId);
    } catch (error) {
      console.warn('Synchronizing push identity failed; it will be retried.', error);
    }
  }

  private async loadPushNotifications(): Promise<PushNotificationService> {
    try {
      this.pushNotificationsPromise ??= import('./core/pwa/push-notification.service').then(
        ({ PushNotificationService }) => this.injector.get(PushNotificationService),
      );
      return await this.pushNotificationsPromise;
    } catch (error) {
      this.pushNotificationsPromise = null;
      throw error;
    }
  }
}
